<template>
  <div v-if="activity" class="mx-2">
    <b-row class="my-2">
      <b-col md="12">
        <h4>
          <span v-if="disableFields"> {{ activity.name }}</span>
          <b-form-input
            v-if="!disableFields"
            v-model.lazy="$v.activity.name.$model"
            type="text"
            ref="name"
            :state="nameCheck"
            :disabled="disableFields"
            minlength="3"
            maxlength="60"
          ></b-form-input>
        </h4>
        <router-link v-if="!activity.isNew && disableFields" :to="{ name: 'controlactivity', params: { id: activity.id } }">
          {{ $tc("link") }}
        </router-link>
        <error-list :field="$v.activity.name"></error-list>
      </b-col>
    </b-row>
    <b-row v-if="!activity.isNew && (activity.info || activity.answer)">
      <b-col md="12" class="my-2">
        {{ $tc("document") }}
        <router-link v-if="activity.info" :to="{ name: 'inforouter', params: { id: activity.info.id } }">
          {{ activity.info.name }}
        </router-link>
        <router-link
          v-if="activity.answer"
          :to="{
            name: 'protocolrouter',
            params: { id: activity.answer.protocol.id }
          }"
        >
          {{ activity.answer.protocol.questionnaire.name }}
        </router-link>
      </b-col>
    </b-row>
    <b-row class="my-2">
      <b-col lg="7">
        <p class="mb-n1" @click="clickCounter++">{{ $t("description") }}</p>
        <text-editor
          class="shadow"
          :disabled="disableFields"
          v-model="activity.description"
          :minHeight="10"
          p_showMenu="dynamic"
          :p_click="clickCounter"
        ></text-editor>
      </b-col>
      <b-col lg="5">
        <span v-if="disableFields">
          <p class="mb-n1">{{ $tc("responsible") }}</p>
          <h5 v-if="activity.responsible">
            {{ activity.responsible.fullName }}
          </h5>
        </span>
        <responsible-selector
          v-if="!disableFields"
          :editedActivity="activity"
          :disabled="disableFields || activity.status > 2 || isSigned"
        />
        <p class="mt-2 mb-n1">{{ $t("deadline") }}</p>
        <b-input-group>
          <strong v-if="disableFields">
            <b-badge v-if="activity.deadline_date" :variant="activityBadgeStatus">
              {{ activity.deadline_date }}
            </b-badge>
          </strong>
          <b-form-input
            v-if="!disableFields"
            v-model.lazy="$v.activity.deadline_date.$model"
            v-model="activity.deadline_date"
            type="date"
            :disabled="disableFields"
          />
          <error-list :field="$v.activity.deadline_date"></error-list>
        </b-input-group>
      </b-col>
    </b-row>
    <b-row class="my-4">
      <b-col md="9" v-if="disableFields">
        <strong class="mt-2 mb-n1">{{ $t("status") }}</strong>
        <h5 v-if="!showEdit">{{ $t("types.activityStatusTypes." + activity.status) }}</h5>
        <b-form-group v-if="showEdit" v-slot="{ ariaDescribedby }">
          <b-form-radio-group
            id="radio-group-2"
            v-model="activity.status"
            :aria-describedby="ariaDescribedby"
            name="radio-sub-component"
            size="lg"
            @change="sign"
          >
            <b-form-radio value="1">
              {{ $t("types.activityStatusTypes.1") }}
            </b-form-radio>
            <b-form-radio value="2">
              {{ $t("types.activityStatusTypes.2") }}
            </b-form-radio>
            <b-form-radio value="3">
              {{ $t("types.activityStatusTypes.3") }}
            </b-form-radio>
            <b-form-radio value="4">
              {{ $t("types.activityStatusTypes.4") }}
            </b-form-radio>
          </b-form-radio-group>
        </b-form-group>
        <span v-if="activity.signature">{{ activity.signature }}</span>
      </b-col>
      <b-col md="6" v-if="!disableFields">
        <strong class="mt-2 mb-n1">{{ $t("status") }}</strong>
        <b-form-select
          v-if="!disableFields"
          v-model="activity.status"
          :options="activityStatusTypes"
          :disabled="disableFields"
          @change="sign"
        ></b-form-select>
        <span v-if="activity.signature">{{ activity.signature }}</span>
      </b-col>
      <b-col md="3" v-if="showEdit && activityDone">
        <check-button
          v-if="!isSigned && !signActive"
          icon="fa-pen"
          variant="default"
          size="sm"
          class="mr-2"
          @accepted="activateSign">
          {{ $tc("readReceiptSign") }}
        </check-button>
        <check-button
          v-if="signActive"
          icon="fa-check"
          className="outline-success"
          size="sm"
          class="mr-2"
          :require-confirm="true"
          @accepted="signActivity()">
          {{ $tc("save") }}
        </check-button>
        <check-button
          v-if="(isSigned || signActive) && !disableFields"
          icon="fa-times"
          className="outline-warning"
          size="sm"
          class="mr-2"
          :require-confirm="true"
          @accepted="clearSignature">
          {{ $tc("clear") }}
        </check-button>
      </b-col>
    </b-row>
    <b-row class="my-2">
      <b-col md="12" v-if="activityDone">
        <VueSignaturePad
          v-if="signActive || isSigned"
          height="100px"
          width="320px"
          :options="signOptions"
          ref="signaturePad"
          style="border: 1px solid #000;"
          class="mb-2"/>
      </b-col>
    </b-row>
    <b-row class="my-2 custom-rounded-border" v-if="activity.isDone && (!disableFields || activity.done_date || activity.result)">
      <b-col md="4" v-if="activity.activity_type != 1">
        <p class="mb-n1">{{ $t("done") }}</p>
        <strong v-if="disableFields && activity.done_date">
          {{activity.done_date}}
        </strong>
        <b-form-input
          v-if="!disableFields"
          cy-id="deadline"
          v-model="activity.done_date"
          type="date" />
      </b-col>
      <b-col md="8" v-if="activity.activity_type != 1 && (!disableFields || (activity.result && activity.result.length > 7))">
        <strong class="mb-n2" @click="clickCounter++">
          {{ $t("result") }}
        </strong>
        <text-editor
          v-if="activity.result || !disableFields"
          :disabled="disableFields"
          v-model="activity.result"
          :minHeight="5"
          p_showMenu="dynamic"
          :p_click="clickCounter"
        ></text-editor>
      </b-col>
    </b-row>
    <b-row>
      <b-col v-if="activity.controller != null && activity.status > 2" class="custom-rounded-border my-2">
        <activity-controlled :editedActivity="activity" :p_disableFields="!editFields
 || !isController" class="my-2" />
      </b-col>
    </b-row>
    <b-row>
      <b-col md="4">
        <p v-if="!disableFields" @click="showInfoMeta = !showInfoMeta" class="pointer">
          {{ $t("extendedInformation") }}
          <i v-if="!showInfoMeta" class="fa fa-angle-down"></i>
          <i v-if="showInfoMeta" class="fa fa-angle-up"></i>
        </p>
      </b-col>
    </b-row>
    <b-row class="my-2" v-if="showInfoMeta || activity.controller || activity.field3">
      <b-col md="4" v-if="disableFields && activity.controller">
        <p class="mb-n1">{{ $tc("controller") }}</p>
        <h6>
          {{ activity.controller.fullName }}
        </h6>
      </b-col>
      <b-col md="4" v-if="!disableFields && (showInfoMeta || activity.controller)">
        <controller-selector
          :editedActivity="activity"
          :disabled="false"
        />
      </b-col>
      <b-col md="4" v-if="showInfoMeta || activity.field3">
        <p class="mb-n1">{{ $tc("department") }}</p>
        <strong v-if="disableFields">
          {{ activity.field3 }}
        </strong>
        <b-form-select cy-id="department" v-if="departments && !disableFields" v-model="activity.field3" :disabled="disableFields">
          <template slot="first">
            <option :value="activity.field3" cy-selected>
              {{ activity.field3 }}
            </option>
            <option :value="null"></option>
          </template>
          <b-form-select-option v-for="department in departments" :key="department.id" :value="department.name">
            {{ department.name }}
          </b-form-select-option>
        </b-form-select>
      </b-col>
      <b-col md="4" v-if="!disableFields || activity.category">
        <p class="mb-n1">{{ $t("category") }}</p>
        <strong v-if="disableFields">
          {{ activity.category ? activity.category.name : "" }}
        </strong>
        <b-form-select
          v-if="activityCategories && !disableFields"
          v-model="activity.category"
          :disabled="disableFields">
          <template slot="first">
            <option v-if="activity.category" :value="activity.category">
              {{ activity.category.name }}
            </option>
          </template>
          <b-form-select-option
            v-for="activityCategory in activityCategories"
            :key="activityCategory.id"
            :value="activityCategory"
          >
            {{ activityCategory.name }}
          </b-form-select-option>
        </b-form-select>
      </b-col>
      <b-col md="4" v-if="!disableFields && $userCan('change_company') && showInfoMeta">
        <p class="mb-n1">{{ $t("createNewCategory") }}:</p>
        <b-form-input v-model="newCategory.name" type="text" :disabled="disableFields"></b-form-input>
        <b-button
          variant="success"
          @click="saveActivityCategory()"
          :disabled="loading"
          v-if="!disableFields && newCategory.name"
        >
          <strong class="mr-2">{{ $t("add") }}</strong>
          <span class="icon">
            <i class="fa fa-plus"></i>
          </span>
        </b-button>
      </b-col>
      <!--
      <b-col md="6">
        <p class="mb-n1">{{ $tc("questionnaire") }}</p>
        <b-form-select
          v-if="companyQuestionnaires"
          v-model="activity.questionnaire"
          :disabled="disableFields"
        >
          <template slot="first">
            <option
              v-if="activity.questionnaire"
              :value="activity.questionnaire"
              >{{ activity.questionnaire.name }}</option
            >
          </template>
          <b-form-select-option
            v-for="companyQuestionnaire in companyQuestionnaires"
            :key="companyQuestionnaire.id"
            :value="companyQuestionnaire.questionnaire"
            >{{ companyQuestionnaire.questionnaire.name }}</b-form-select-option
          >
        </b-form-select>
      </b-col>
      -->
      <b-col md="4" v-if="showInfoMeta || this.activity.tags.length > 0">
        <tag-selector
          :p_disableFields="disableFields"
          :p_selectedTags="activity.tags"
          :p_readOnly="false"
          @tagsUpdated="updateTags"
        ></tag-selector>
      </b-col>
      <b-col md="4" v-if="showInfoMeta || activity.field4">
        <p class="mb-n1">{{ $t("priority") }}</p>
        <strong v-if="disableFields && activity.field4">
          {{ $t("types.activityConsequenceTypes." + activity.field4) }}
        </strong>
        <b-form-select
          v-if="!disableFields"
          cy-id="predicted-impact"
          v-model="activity.field4"
          :options="activityConsequenceTypes"
        />
      </b-col>
    </b-row>
    <b-row v-if="!activity.isNew && showEdit">
      <b-col v-if="$userCan('change_activity')" md="12" class="mt-4">
        <b-button variant="outline-dark" @click="activateEdit()">
          <span class="icon">
            <i class="fa fa-edit"></i>
            {{ $t("modify") }}
          </span>
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import CompanyQuestionnaireService from "@/services/CompanyQuestionnaireService";
import Activity from "@/models/Activity";
import ActivityCategory from "@/models/ActivityCategory";
import PersonService from "@/services/PersonService";
import ActivityCategoryService from "@/services/ActivityCategoryService";
import TextEditor from "@/components/TextEditor/TextEditor";
import moment from "moment";
import DepartmentService from "@/services/DepartmentService";
import { Department } from "@/models/Department";
import { ACTIVITY_STATUS_TYPES, ACTIVITY_CONTROLLED_STATUS_TYPES  } from "@/constants";
import ActivityControlled from "@/components/Activities/ActivityControlled";
import ResponsibleSelector from "@/components/Activities/ActivityResponsibleSelector";
import ControllerSelector from "@/components/Activities/ActivityControllerSelector";
import TagSelector from "@/components/TagSelector.vue";

export default {
  name: "ActivityModalForm",
  components: { TextEditor, ActivityControlled, ResponsibleSelector, ControllerSelector, TagSelector },
  props: {
    editedActivity: {
      type: Activity,
      required: false,
      default: null
    },
    showEdit: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      activity: this.editedActivity,
      activityCategories: [],
      companyQuestionnaires: [],
      editFields: false,
      loading: false,
      newCategory: new ActivityCategory({}),
      inputValid: null,
      departments: [],
      signOptions: {
        minWidth: 1,
        maxWidth: 1.5
      },
      signActive: false,
      clickCounter: 0,
      showInfoMeta: false
    };
  },
  validations: {
    activity: Activity.validations
  },
  mounted() {
    //this.getQuestionnaires();
    this.getActivityCategories();
    this.getDepartments();
    this.checkActivityName(this.nameCheck);
    this.signPadConfig();
  },
  watch: {
    editedActivity(activity) {
      this.activity = activity;
      if (this.activity) {
        this.signPadConfig();
      }
    },
    nameCheck(valid) {
      this.checkActivityName(valid);
    },
    signActive() {
      //Dont get emit with parameter to work. So use 2 different functions
      if (this.signActive) {
        this.$emit("signOngoing");
      } else {
        this.$emit("signDisabled");
      }
    }
  },
  computed: {
    ...mapGetters("constants", ["activityStatusTypes", "activityConsequenceTypes"]),
    disableFields() {
      return !(this.activity.isNew || this.editFields) || !this.showEdit;
    },
    nameCheck() {
      return this.activity && this.activity.name && this.activity.name.length > 2 && this.activity.name.length < 60
        ? true
        : false;
    },
    activityDone() {
      return this.activity && this.activity.status == ACTIVITY_STATUS_TYPES.DONE;
    },
    isSigned() {
      return this.activity.field6 != null && this.activity.field6 != "";
    },
    isController() {
      return this.activity.controller != null && this.$user.person == this.activity.controller.id;
    },
    activityBadgeStatus() {
      if(this.activity && this.activity.deadline_date)
      {
        if (this.activity.isOverdue && this.activity.status != ACTIVITY_STATUS_TYPES.DONE) {
          return "danger";
        } else if (this.activity.isNextWeek && this.activity.status != ACTIVITY_STATUS_TYPES.DONE) {
          return "warning";
        } else if (
          this.activity.status == ACTIVITY_STATUS_TYPES.DONE &&
          this.activity.controller != null &&
          this.activity.controlled_status != ACTIVITY_CONTROLLED_STATUS_TYPES.APPROVED
        ) {
          return "warning";
        }
      }
    },
  },
  methods: {
    updateTags(tags) {
      this.activity.tags = tags;
    },
    getActivityCategories() {
      return ActivityCategoryService.getActivityCategoriesByCompanyIncludingSystem(this.$userSelectedCompanyId()).then(
        (activityCategories) => {
          this.activityCategories = activityCategories;
        }
      );
    },
    getQuestionnaires() {
      CompanyQuestionnaireService.getCompanyQuestionnairesByCompanyAndIsActive(this.$userSelectedCompanyId()).then(
        (companyQuestionnaires) => {
          this.companyQuestionnaires = companyQuestionnaires;
        }
      );
    },
    sign() {
      const today = moment().format("YYYY-MM-DD");
      this.activity.signature = this.$user.first_name + " " + this.$user.last_name + " - " + today;
    },
    saveActivityCategory() {
      this.createActivityCategory();
    },
    createActivityCategory() {
      ActivityCategoryService.createCompanyActivityCategory(this.$userSelectedCompanyId(), this.newCategory).then(
        (category) => {
          let newCategoryId = category.id;
          this.newCategory = new ActivityCategory({});

          // Re-fetch list so we get whole object for new category
          this.getActivityCategories().then(() => {
            this.activity.category = this.activityCategories.find((c) => c.id === newCategoryId);
          });
        }
      );
    },
    checkActivityName(valid) {
      if (this.inputValid != valid) {
        this.inputValid = valid;
        this.$emit("validInput", this.inputValid);
      }
    },
    getDepartments() {
      DepartmentService.getDepartmentsByCompany(
        this.$userSelectedCompanyId(),
        false //Should be true
      ).then((departments) => {
        this.departments = departments;
      });
    },
    updateDepartment(person) {
      if (person && person.departments.length > 0 && !this.activity.field3) {
        this.activity.field3 = person.departments[0].name;
      }
    },
    activateEdit(activate) {
      if (activate) {
        this.editFields = true;
      } else {
        this.editFields = !this.editFields;
      }

      if (this.editFields) {
        this.signPadConfig();
      }
    },
    activateSign() {
      this.signActive = true;

      if (!this.isSigned && this.$refs.signaturePad) {
        this.$refs.signaturePad.openSignaturePad();
      }
    },
    clearSignature() {
      if (this.$refs.signaturePad) {
        this.$refs.signaturePad.clearSignature();
      }

      if (this.isSigned) {
        this.activity.field6 = "";
        this.signActive = false;
        this.$emit("signed");

        if (this.$refs.signaturePad) {
          this.$refs.signaturePad.lockSignaturePad();
        }
      }
    },
    signActivity() {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      this.activity.field6 = data;
      this.signActive = false;
      this.$refs.signaturePad.lockSignaturePad();
      this.$emit("signed");
    },
    signPadConfig() {
      if (this.activity && this.activity.field6) {
        if (this.$refs.signaturePad && this.$refs.signaturePad.signaturePad.canvas.height > 0) {
          this.$refs.signaturePad.fromDataURL(this.activity.field6);
          this.$refs.signaturePad.lockSignaturePad();
        } else {
          this.$nextTick(() => {
            this.$refs.signaturePad.resizeCanvas();
            this.signPadConfig();
          });
        }
      } else if (this.$refs.signaturePad) {
        this.$refs.signaturePad.lockSignaturePad();
        this.$nextTick(() => {
          if (this.$refs.signaturePad) {
            this.$refs.signaturePad.resizeCanvas();
          }
        });
      }
    },
  }
};
</script>

<style scoped lang="scss"></style>
